import React, { useState, useRef } from 'react';
import Nav from '../components/nav';
import SEO from '../components/SEO';
import styles from '../styles/signup.module.css';
import axios from 'axios';
import { Film } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { baseUrl } from '../config';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/high-res.css';
import studentProgramBanner2 from '../assets/img/student-program2.jpg';

const SignUp = () => {
  const [userType, setUserType] = useState('');
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordValidationError, setPasswordValidationError] = useState('');
  const [marketingInterest, setMarketingInterest] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const [role, setRole] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const retryTimerRef = useRef(null);

  // ── Check if error is timeout/gateway related ─────────────────
  const isTimeoutLike = (status, detail) => {
    if ([502, 503, 504].includes(status)) return true;
    if (typeof detail === 'string') {
      const d = detail.toLowerCase();
      return d.includes('timeout') || d.includes('service') || d.includes('gateway');
    }
    return false;
  };

  // ── Silent retry — spinner stays, user sees nothing ───────────
  const scheduleRetry = (fn) => {
    setIsLoading(true);
    setErrorMessage('');
    retryTimerRef.current = setTimeout(() => { fn(); }, 90000);
  };

  const cancelRetry = () => {
    clearTimeout(retryTimerRef.current);
    setIsLoading(false);
  };
  // ─────────────────────────────────────────────────────────────

  // Returns: true = success | false = real error | 'timeout' = retry silently
  const signup = async () => {
    setErrorMessage("");
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username, password, email,
        phone_number: phone,
        country: "",
        user_type: userType,
        year_experience: 0,
        role
      }),
    };
    try {
      const response = await fetch(`${baseUrl}/signup`, requestOptions);
      const data = await response.json();

      if (!response.ok) {
        const detail = data.detail;
        // Timeout from backend — retry silently, show nothing
        if (isTimeoutLike(response.status, detail)) return 'timeout';
        // Real validation error — show to user
        if (typeof detail === 'string') {
          setErrorMessage(detail + "!");
        } else if (Array.isArray(detail)) {
          setErrorMessage(detail.map(d => d.msg || JSON.stringify(d)).join(", ") + "!");
        } else {
          setErrorMessage("Something went wrong. Please try again.");
        }
        return false;
      }
      return true;
    } catch {
      // Network error — treat as timeout, retry silently
      return 'timeout';
    }
  };

  const attemptSignup = async () => {
    setIsLoading(true);
    const signupStatus = await signup();

    // Timeout — keep spinner, retry after 90s silently
    if (signupStatus === 'timeout') {
      scheduleRetry(attemptSignup);
      return;
    }

    if (signupStatus === true) {
      try {
        const response = await axios.post(
          `${baseUrl}/login`,
          new URLSearchParams({ username, password }),
          {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            timeout: 90000
          }
        );
        const { access_token, redirect_url } = response.data;
        localStorage.setItem('access_token', access_token);
        navigate(redirect_url);
      } catch (error) {
        const status = error.response?.status;
        const isTimeout =
          error.code === 'ECONNABORTED' || [502, 503, 504].includes(status);
        if (isTimeout) {
          // Login timeout — retry silently
          scheduleRetry(attemptSignup);
        } else {
          setIsLoading(false);
        }
      }
    } else {
      // Real validation error — stop loading
      setIsLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    cancelRetry();
    await attemptSignup();
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const response = await axios.post(
        `${baseUrl}/auth/google`,
        {
          token: credentialResponse.credential,
          email: decoded.email, name: decoded.name,
          picture: decoded.picture, google_id: decoded.sub
        },
        { timeout: 90000 }
      );
      const { access_token, redirect_url, user } = response.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user_dashboard', redirect_url);
      if (!user.profile_completed) {
        localStorage.setItem('google_user_data', JSON.stringify({
          email: user.email, name: user.name, picture: user.picture
        }));
      }
      navigate(redirect_url);
    } catch (error) {
      const status = error.response?.status;
      const isTimeout =
        error.code === 'ECONNABORTED' || [502, 503, 504].includes(status);
      if (isTimeout) {
        // Google timeout — retry silently
        scheduleRetry(() => handleGoogleSuccess(credentialResponse));
      } else {
        setErrorMessage(error.response?.data?.detail || 'Google signup failed. Please try again.');
        setIsLoading(false);
      }
    }
  };

  const handleGoogleError = () => {
    setErrorMessage('Google signup failed. Please try again.');
  };

  const validatePassword = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*\d)[A-Za-z\d!@#$%^&*]{10,}$/;
    return regex.test(password);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (!validatePassword(newPassword)) {
      setPasswordValidationError('Password must be at least 10 characters long, include 1 capital letter, 1 special character, and 1 numerical digit.');
    } else {
      setPasswordValidationError('');
    }
    validatePasswordMatch(newPassword, confirmPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    validatePasswordMatch(password, newConfirmPassword);
  };

  const validatePasswordMatch = (password, confirmPassword) => {
    if (password !== confirmPassword) setPasswordError('Passwords do not match');
    else setPasswordError('');
  };

  const handleUserTypeChange = (e) => { setUserType(e.target.value); };

  const isUsernameUnique = async (username) => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    const existingUsernames = ['user1', 'admin', 'test123'];
    return !existingUsernames.includes(username);
  };

  const validateUsername = async (username) => {
    const regex = /^(?=.*[a-z])(?=.*\d)[a-z\d]{5,}$/;
    if (!regex.test(username)) {
      setUsernameError('Username must contain both lowercase letters and numbers and be at least 5 characters long.');
      return;
    }
    const unique = await isUsernameUnique(username);
    if (!unique) setUsernameError('Username is already taken.');
    else setUsernameError('');
  };

  const handleUsernameChange = async (e) => {
    const newUsername = e.target.value.toLowerCase();
    setUsername(newUsername);
    await validateUsername(newUsername);
  };

  const filmReels = [...Array(20)].map((_, i) => ({
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    rotation: Math.random() * 360,
  }));

  return (
    <div>
      <SEO
        title="Sign Up"
        description="Create your free ProducerBuddy account and start managing your film productions with AI-powered tools today."
        url="https://producerbuddy.pilomation.com/signup"
        robots="noindex, nofollow"
      />
      <Nav />
      <div className="absolute inset-0 overflow-hidden opacity-10">
        {filmReels.map((reel, i) => (
          <div key={i} className="absolute animate-pulse"
            style={{ left: reel.left, top: reel.top, transform: `rotate(${reel.rotation}deg)` }}>
            <Film className="w-16 h-16 text-cyan-300" />
          </div>
        ))}
      </div>

      <div className={styles.signupContainer}>
        <div className="pb-login-container relative max-w-4xl w-full">
          <div className="pb-login-card bg-gray-800 p-12 rounded-lg shadow-xl border border-cyan-300/30">

            {isLoading && (
              <div className={styles.signupLoadingOverlay}>
                <div className={styles.signupLoadingSpinner}></div>
                <p className={styles.signupLoadingText}>Setting up your account...</p>
              </div>
            )}

            <h2 className={styles.signupTitle}>Create Your Account</h2>

            {/* ── Google Button — Highlighted ── */}
            <div className="mb-8">
              <p className={styles.googleRecommendedBadge}>
                ⚡ Fastest — One Click Signup
              </p>
              <div className={styles.googleButtonWrapper}>
                <div className={styles.googleButtonGlow} />
                <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    useOneTap={false}
                    theme="filled_black"
                    size="large"
                    text="signup_with"
                    shape="rectangular"
                    width="100%"
                  />
                </div>
              </div>
              <div className="pb-divider my-6">
                <span className="bg-gray-800 text-teal-400 text-md px-4">OR SIGN UP WITH EMAIL</span>
              </div>
            </div>

            <form className={styles.signupForm} onSubmit={submit}>

              <div className={styles.signupFormGroup}>
                <label className={styles.signupRequired}>Select User Type</label>
                <select className={styles.signupSelect} value={userType} onChange={handleUserTypeChange} required disabled={isLoading}>
                  <option value="" disabled>Select user type</option>
                  <option value="enthusiast">Film Enthusiast</option>
                  <option value="lead">Production Lead</option>
                  <option value="marketing">Marketing Enthusiast</option>
                </select>
              </div>

              <div className={styles.signupFormGroup}>
                <label className={styles.signupRequired}>Username</label>
                <input className={styles.signupInput} type="text" value={username}
                  onChange={handleUsernameChange} required disabled={isLoading} />
                {usernameError && <p className={styles.signupErrorText}>{usernameError}</p>}
              </div>

              <div className={styles.signupFormGroup}>
                <label className={styles.signupRequired}>Email</label>
                <input className={styles.signupInput} type="email" required
                  onChange={(e) => setEmail(e.target.value)} disabled={isLoading} />
              </div>

              <div className={styles.signupFormGroup}>
                <label className={styles.signupRequired}>Phone Number</label>
                <div className="relative w-full" translate="no">
                  <PhoneInput
                    country={'in'}
                    value={phone}
                    onChange={setPhone}
                    containerClass="phoneFieldFix"
                    inputClass={styles.signupInput}
                    inputProps={{ required: true, disabled: isLoading }}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className={styles.signupFormGroup}>
                <label className={styles.signupRequired}>Password</label>
                <input className={styles.signupInput} type="password" value={password}
                  onChange={handlePasswordChange} required disabled={isLoading} />
                {passwordValidationError && (
                  <p className={styles.signupPasswordValidationError}>{passwordValidationError}</p>
                )}
              </div>

              <div className={styles.signupFormGroup}>
                <label className={styles.signupRequired}>Confirm Password</label>
                <input className={styles.signupInput} type="password" value={confirmPassword}
                  onChange={handleConfirmPasswordChange} required disabled={isLoading} />
                {passwordError && <p className={styles.signupPasswordError}>{passwordError}</p>}
              </div>

              {userType === 'enthusiast' && (
                <div className={styles.signupFormGroup}>
                  <label className={styles.signupRequired}>What You Are Looking For</label>
                  <select className={styles.signupSelect} defaultValue="" required
                    onChange={(e) => setRole(e.target.value)} disabled={isLoading}>
                    <option value="" disabled>Select what you are looking for</option>
                    <option value="leadActor">Lead Actor/Performer</option>
                    <option value="creativeDirector">Creative Director/Writer</option>
                    <option value="musicSoundExpert">Music/Sound Expert</option>
                    <option value="visualArtSpecialist">Visual/Art Specialist</option>
                    <option value="scriptStoryDeveloper">Script/Story Developer</option>
                    <option value="productionCoordination">Production/Coordination Lead</option>
                    <option value="editingPostProduction">Editing/Post-Production</option>
                  </select>
                </div>
              )}

              {userType === 'lead' && (
                <div className={styles.signupFormGroup}>
                  <label className={styles.signupRequired}>Production Specialty</label>
                  <select className={styles.signupSelect} required disabled={isLoading}>
                    <option value="" disabled>Select specialty</option>
                    <option value="direction">Direction</option>
                    <option value="cinematography">Cinematography</option>
                    <option value="editing">Editing</option>
                    <option value="sound">Sound</option>
                  </select>
                </div>
              )}

              {userType === 'marketing' && (
                <div className={styles.signupFormGroup}>
                  <label className={styles.signupRequired}>Marketing Interest</label>
                  <select className={styles.signupSelect} value={marketingInterest}
                    onChange={(e) => setMarketingInterest(e.target.value)} required disabled={isLoading}>
                    <option value="" disabled>Select your interest</option>
                    <option value="socialMedia">Social Media</option>
                    <option value="emailMarketing">Email Marketing</option>
                    <option value="contentCreation">Content Creation</option>
                    <option value="seo">SEO</option>
                    <option value="paidAdvertising">Paid Advertising</option>
                  </select>
                </div>
              )}

              <div className={styles.signupCheckboxContainer}>
                <input className={styles.signupCheckbox} type="checkbox" required disabled={isLoading} />
                <label className={styles.signupRequired}>
                  I agree to the{' '}
                  <a href="/terms" style={{ textDecoration: 'underline', color: '#0a73e1' }}>
                    terms and conditions
                  </a>
                </label>
              </div>

              {errorMessage !== "" && (
                <p className={styles.signupErrorMsg}>{errorMessage}</p>
              )}

              <button type="submit" className={styles.signupButton} disabled={isLoading}>
                {isLoading ? 'Creating Account...' : 'Sign Up'}
              </button>

              <p className="text-center text-sm text-gray-400 mt-4">
                Student at a partnered institute?{' '}
                <Link to="/student-program" className="text-teal-300 hover:text-teal-200 underline">
                  Explore the Student Program
                </Link>
              </p>

            </form>
          </div>
        </div>
      </div>

      <Link to="/student-program" className="block w-full mt-12 mb-16">
        <img
          src={studentProgramBanner2}
          alt="Student Program"
          className="w-full h-auto"
        />
      </Link>
    </div>
  );
};

export default SignUp;